// @ts-strict

import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DatePickerService } from '@app/core/services/date-picker/date-picker.service';
import { StatisticsSummaryService } from '@app/core/services/statistics/statistics-summary.service';
import { ToggleSidebarService } from '@app/core/services/toggle-sidebar/toggle-sidebar.service';
import { ExportReportsComponent } from '@app/shared/components/statistics-top-menu/export-reports/export-reports.component';
import { NgbDate, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { AvailableDatePeriod } from './range-picker/date-picker-sidebar/statistics-date-picker-sidebar.component';
import { StatisticsDatePickerService } from './range-picker/date-picker/custom-date-picker.service';
import { StatisticsTopMenuNewService } from './statistics-top-menu-new.service';

export type StatisticsDatePickers = 'default' | 'compare';
export interface IDateRange {
  range: AvailableDatePeriod;
  dateFrom: NgbDate;
  dateTo: NgbDate;
}

@Component({
  selector: 'app-statistics-top-menu-new',
  templateUrl: './statistics-top-menu-new.component.html',
  styleUrls: ['./statistics-top-menu-new.component.scss'],
  providers: [StatisticsDatePickerService]
})
export class StatisticsTopMenuNewComponent implements OnInit, OnDestroy {
  public currentDatePicker: StatisticsDatePickers = 'default';

  public isRangePickerVisible = false;
  public isSidebarClosed = false;
  public comparisonActivated = false;
  public compareButtonClicked = false;

  public INITIAL_DEFAULT_PICKER_DATE_PERIOD: AvailableDatePeriod = 'last_30_days';
  public INITIAL_COMPARE_PICKER_DATE_PERIOD: AvailableDatePeriod = 'user';

  public defaultButtonDateRangeTitle: string = '';
  public compareButtonDateRangeTitle: string = this.translate.instant('view_translations.choose_range');

  public defaultButtonDateRange = '';
  public compareButtonDateRange = '';

  public selectedPeriodDates?: IDateRange = undefined;

  public compareSelectedPeriodDates?: IDateRange = undefined;

  public defaultPickerDateRanges: IDateRange[] = [];
  public comparePickerDateRanges: IDateRange[] = [];

  private destroy$ = new Subject<void>();

  get isDefaultDatePicker(): boolean {
    return this.currentDatePicker === 'default';
  }

  get isCompareDatePicker(): boolean {
    return this.currentDatePicker === 'compare';
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly sidebarService: ToggleSidebarService,
    private readonly modalService: NgbModal,
    private readonly statistics: StatisticsSummaryService,
    private readonly statisticsDatePickerService: StatisticsDatePickerService,
    private readonly statisticsTopMenuNewService: StatisticsTopMenuNewService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.setInitialDefaultPickerSelectedPeriodDates();

    this.sidebarService.getToggleStatus
      .pipe(
        tap(res => {
          this.isSidebarClosed = res;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.compareMode
      .pipe(
        tap(res => {
          this.comparisonActivated = res.compareMode;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.compareButtonInactivated
      .pipe(
        tap(res => {
          if (res) {
            this.compareButtonClicked = false;
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setInitialDefaultPickerSelectedPeriodDates();
        this.resetCompareButton();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openDatePicker(currentDatePicker: StatisticsDatePickers): void {
    if (this.isRangePickerVisible && this.currentDatePicker === currentDatePicker) {
      this.isRangePickerVisible = !this.isRangePickerVisible;

      return;
    }

    this.currentDatePicker = currentDatePicker;

    this.isRangePickerVisible = true;
  }

  public closeRangePicker(): void {
    this.isRangePickerVisible = false;
  }

  public getDateRangeForDefaultPicker(selectedPeriodDates: IDateRange): void {
    const { dateFrom, dateTo, range } = selectedPeriodDates;

    this.selectedPeriodDates = selectedPeriodDates;
    this.statistics.dateRange.next({ dateFrom, dateTo });

    this.setUserRangeForComparePicker(selectedPeriodDates, true);
    this.setDefaultPickerButtonTitle(selectedPeriodDates);
    this.setMaxCompareRange(selectedPeriodDates);

    if (this.isUserRange(range)) this.setUserRangeForDefaultPicker(selectedPeriodDates);

    if (this.compareButtonClicked) this.compareActiveHandler();

    this.isRangePickerVisible = !this.isRangePickerVisible;

    this.resetCompareButton();
  }

  public getDateRangeForComparePicker(compareSelectedPeriodDates: IDateRange): void {
    const { dateFrom, dateTo, range } = compareSelectedPeriodDates;

    this.compareSelectedPeriodDates = compareSelectedPeriodDates;
    this.statistics.compareModeHandler(true, { dateFrom, dateTo });

    this.setComparePickerButtonTitle(compareSelectedPeriodDates);

    if (this.isUserRange(range)) this.setUserRangeForComparePicker(compareSelectedPeriodDates);

    this.isRangePickerVisible = false;
  }

  public openModal(): void {
    this.modalService.open(ExportReportsComponent, { size: 'me', centered: true });
  }

  public compareActiveHandler(): void {
    this.compareButtonClicked = !this.compareButtonClicked;

    if (!this.compareButtonClicked) {
      this.statistics.resetCompareMode();
    }

    if (this.compareButtonClicked && this.compareSelectedPeriodDates) {
      this.getDateRangeForComparePicker(this.compareSelectedPeriodDates);
    }
  }

  private setUserRangeForDefaultPicker = (selectedPeriodDates: IDateRange) => {
    const { dateFrom, dateTo } = selectedPeriodDates;

    this.defaultPickerDateRanges = this.statisticsDatePickerService.setUserDateRanges({ dateFrom, dateTo });
  };

  private setUserRangeForComparePicker = (selectedPeriodDates: IDateRange, setBasedOnDefaultPicker = false) => {
    const userRange = setBasedOnDefaultPicker ? this.statisticsDatePickerService.getInitialUserRangeForComparePicker(selectedPeriodDates) : selectedPeriodDates;

    this.comparePickerDateRanges = this.statisticsDatePickerService.setUserDateRanges(userRange);
  };

  private setInitialDefaultPickerSelectedPeriodDates = () => {
    this.defaultPickerDateRanges = this.statisticsDatePickerService.getDateRanges();

    const initialPeriod = this.defaultPickerDateRanges.find(({ range }) => range === this.INITIAL_DEFAULT_PICKER_DATE_PERIOD);

    if (initialPeriod) {
      const { dateFrom, dateTo, range } = initialPeriod;

      this.selectedPeriodDates = { dateFrom, dateTo, range };
      this.setDefaultPickerButtonTitle(this.selectedPeriodDates);
      this.setMaxCompareRange(this.selectedPeriodDates);

      this.setUserRangeForComparePicker(this.selectedPeriodDates, true);
    }
  };

  private setMaxCompareRange = (selectedPeriodDates: IDateRange) => {
    const { dateFrom, dateTo } = selectedPeriodDates;

    this.statisticsTopMenuNewService.setMaxCompareDaysRangeAllowed(this.statisticsDatePickerService.calcDaysDiff(dateFrom, dateTo) + 1);
  };

  private setDefaultPickerButtonTitle(selectedPeriodDates: IDateRange): void {
    this.defaultButtonDateRangeTitle = this.translate.instant('view_translations.period_' + selectedPeriodDates.range);
    this.defaultButtonDateRange = this.statisticsTopMenuNewService.getButtonDateRange(selectedPeriodDates);
  }

  private setComparePickerButtonTitle(compareSelectedPeriodDates: IDateRange): void {
    this.compareButtonDateRangeTitle = this.translate.instant('view_translations.period_' + compareSelectedPeriodDates.range);
    this.compareButtonDateRange = this.statisticsTopMenuNewService.getButtonDateRange(compareSelectedPeriodDates);
  }

  private resetCompareButton = () => {
    this.compareButtonDateRangeTitle = this.translate.instant('view_translations.choose_range');
    this.compareButtonDateRange = '';
    this.compareSelectedPeriodDates = undefined;
  };

  private isUserRange = (range: AvailableDatePeriod): boolean => {
    return range === 'user';
  };
}
